import type { ComponentDocs } from '../component-docs.js';
import type { CaosCodeEditor } from './code-editor.js';

/**
 * What `<caos-code-editor>` says about itself.
 *
 * The distinction worth stating up front, because it is the mistake this piece invites: CodeEditor
 * edits ONE artifact's source as text. Inspector edits the PROPERTIES of a selection as fields. A
 * screen that wants "name, label, required" wants Inspector and will regret a textarea; a screen
 * that wants the artifact itself wants this.
 *
 * The other thing a reader would otherwise have to work out: this component never saves. It raises
 * `save` and stops. The platform's write path wants the `componentVersion` stated back with the
 * edit, so the surface holds that number and does the write — an editor that wrote on its own would
 * be a second door onto a pipeline that is deliberately single-doored.
 */
const docs: ComponentDocs = {
  aliases: ['code editor', 'source editor', 'text editor', 'script editor', 'code pane', 'monaco', 'codemirror', 'editor pane'],

  answers: [
    'let a developer edit the source of one artifact',
    'show code with line numbers',
    'put an editing pane beside an artifact tree',
    'let somebody read a definition without being able to change it',
    'offer Ctrl+S on a screen that edits code',
  ],

  // A developer tool occupies a whole destination — it is not something to drop onto a record page
  // beside a few fields, and it needs the height.
  placements: ['appPage'],

  inputs: [
    {
      name: 'value',
      // No attribute, deliberately: an artifact is long, and full of the punctuation markup has to
      // escape — in an attribute it would be mangled, and the source would be in the markup.
      type: 'json',
      description:
        'The code. Set it when the artifact loads, and set it again after a save succeeds — each set ' +
        'becomes the baseline `dirty` is measured against, so the editor stops reporting unsaved work.',
    },
    {
      name: 'language',
      attribute: 'language',
      property: true,
      type: 'text',
      description:
        'What the code is, e.g. `json` or `typescript`. Informational: the engine shipped with the ' +
        'platform does not highlight, and an engine that does reads this. Nothing breaks when it is absent.',
    },
    {
      name: 'readonly',
      attribute: 'readonly',
      type: 'boolean',
      description:
        'Present means the code can be read, selected and copied but not changed, and `save` is ' +
        'never raised. Use it for an artifact the person may see but not edit — not as a way to ' +
        'disable the editor while something loads, which reads to a person as "you may not have this". ' +
        'There is a `readOnly` property behind it for a surface that toggles it in script.',
    },
  ],

  events: [
    {
      name: 'change',
      when: 'The person edits the code. Raised on every edit, not on blur, and it bubbles and is composed.',
      carries: '{ value, dirty } — the code as it now stands, and whether it differs from the last value the page set.',
    },
    {
      name: 'save',
      when:
        'The person presses Ctrl+S (Cmd+S on a Mac). Never raised while `readonly` is present. The ' +
        'browser\'s own save-page dialog is suppressed when it is.',
      carries: '{ value } — the code to save. The surface performs the write; this component does not.',
    },
  ],

  state: [
    {
      name: 'dirty',
      type: 'boolean',
      description:
        'Whether the code differs from the last value the page set. Compared against that baseline ' +
        'rather than latched on the first keystroke, so typing a character and deleting it again ' +
        'leaves the editor clean.',
    },
  ],

  // Nothing is projected. The engine owns the inside of the surface, and a slot would be content the
  // engine is free to replace on its next draw.
  slots: [],

  tokens: [
    { token: '--caos-font-mono', controls: 'The typeface of the code and of the line numbers. One ramp for both, or the gutter drifts out of step with the code down a long file.' },
    { token: '--caos-color-surface', controls: 'The ground the code sits on.' },
    { token: '--caos-color-surface-2', controls: 'The ground of the line-number gutter.' },
    { token: '--caos-color-border', controls: 'The outline of the editor and the rule between the gutter and the code.' },
    { token: '--caos-color-text', controls: 'The ink of the code.' },
    { token: '--caos-color-text-muted', controls: 'The ink of the line numbers, and of the code while read-only.' },
    { token: '--caos-radius-sm', controls: 'The corner radius of the editor.' },
    { token: '--caos-focus-ring', controls: 'The ring drawn when the code has keyboard focus.' },
  ],

  parts: [
    { part: 'surface', is: 'the gutter and the code together' },
    { part: 'gutter', is: 'the line numbers' },
    { part: 'input', is: 'the editable code area' },
  ],

  methods: [
    {
      name: 'focusEditor',
      description:
        'Puts the cursor in the code. Use it after opening an artifact, so the person can type ' +
        'without reaching for the mouse.',
      args: [],
    },
  ],

  placing:
    'Fills a destination, usually the right-hand pane beside an artifact tree. Give it a parent with a ' +
    'real height and it stretches to it; given none it stands about twelve lines tall, which is a ' +
    'working editor rather than the two rows a bare textarea would have been. It brings its own border ' +
    'and ground, so it does not want a card around it. One editor per artifact on screen: two editors ' +
    'over the same artifact have two baselines and will disagree about which of them is dirty.',

  whenToUse:
    'A developer is editing the source of one platform artifact and needs to see it as code — with ' +
    'line numbers, without wrapping, and with the keystroke that saves it.',

  insteadUse: [
    {
      name: 'Inspector',
      when:
        'The properties of the selection are what is being edited — name, label, whether a field is ' +
        'required. Those are fields, and a person editing them through a textarea is being asked to ' +
        'hand-write a shape the platform could have drawn for them.',
    },
    {
      name: 'TextField',
      when:
        'It is a sentence, a description, or a note. A code editor over prose disables spellcheck and ' +
        'autocorrect, refuses to wrap, and numbers the lines — every one of which is wrong for prose.',
    },
    {
      name: 'FieldRenderer',
      when:
        'The value belongs to a record and the platform already knows its type. Let the platform draw ' +
        'the right control for it rather than serialising it into a code pane.',
    },
  ],

  accessibility:
    'The editing surface is a plain textarea, which is why it is one: a screen reader, braille ' +
    'display, dictation and native find all already know what to do with it, and each is something a ' +
    'hand-built editor re-implements badly. The component names the surface "Code editor" and takes ' +
    '`aria-label` off the host when a screen has more than one — label them by what they hold, not by ' +
    'what they are. The line numbers are `aria-hidden`: they are a visual aid, and read aloud they ' +
    'would put a number before every line of code. Ctrl/Cmd+S is intercepted only inside the editor. ' +
    'What the surface still owes a person: somewhere visible to see that work is unsaved — `dirty` ' +
    'and `change` are there for that, and a keystroke is not an announcement.',

  examples: [
    {
      key: 'editing',
      label: 'Editing an artifact',
      summary:
        'The editor doing its job: an artifact loaded as code, line numbers beside it, and the page ' +
        'around it holding the save.',
      placement: { component: 'caos-code-editor', inputs: { language: 'json' } },
      host: {
        summary:
          'hands the editor the artifact and performs the save, because the code is a property rather ' +
          'than markup and the write belongs to the surface — the editor asks, it never writes',
        run: (stage) => {
          const editor = stage.querySelector('caos-code-editor') as CaosCodeEditor | null;
          if (!editor) return;
          editor.value = [
            '{',
            '  "type": "rosterPage",',
            '  "key": "people_list",',
            '  "label": "People",',
            '  "object": "user",',
            '  "columns": [',
            '    { "field": "name", "label": "Name" },',
            '    { "field": "email", "label": "Email" }',
            '  ]',
            '}',
          ].join('\n');
          // What a real surface does with `save`: write, then set the value again so the editor's
          // baseline moves and it stops reporting unsaved work. Here the write is the pause.
          editor.addEventListener('save', (e) => {
            const saved = (e as CustomEvent<{ value: string }>).detail.value;
            window.setTimeout(() => {
              editor.value = saved;
            }, 150);
          });
        },
      },
    },
    {
      key: 'readonly',
      label: 'Read-only',
      summary:
        'An artifact somebody may read but not change: the code can be selected and copied, Ctrl+S ' +
        'raises nothing, and the ink says so without a disabled control claiming the page is busy.',
      placement: { component: 'caos-code-editor', inputs: { language: 'json', readonly: true } },
      host: {
        summary: 'sets the code it is showing, for the same reason the first example does',
        run: (stage) => {
          const editor = stage.querySelector('caos-code-editor') as CaosCodeEditor | null;
          if (!editor) return;
          editor.value = ['{', '  "type": "permissionSet",', '  "key": "setup_admin"', '}'].join('\n');
        },
      },
    },
  ],
};

export default docs;
